export function formatMoney(cents) {
  const amount = (cents || 0) / 100;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
}

export function formatShipping(cents) {
  if (!cents) return 'FREE';
  return formatMoney(cents);
}

const CHECKOUT_TOTALS = {
  tax: '.tax-amount',
  orderTotal: '.order-total-amount, .order-summary-total',
  shipping: '.shipping-amount',
  discount: '.discount-amount',
  compareAtPrice: '.checkout-compare-at',
  priceAfterDiscount: '.checkout-discounted-value',
  shownWithDiscount: '.js-checkout-discount',
  shownWithoutDiscount: '.js-checkout-full-price',
  savePercent: '.m-checkout-mf-summary__save',
  percentOffBadge: '.m-checkout-mf-item__badge',
  percentOffText: '[data-checkout-percent]',
  discountLabel: '.checkout-discount-label',
  deliveryFeeRow: '.checkout-delivery-fee-row',
  deliveryFeeLabel: '.checkout-delivery-fee-label',
  deliveryFeeAmount: '.checkout-delivery-fee-amount',
};

function forEachNode(selector, callback) {
  document.querySelectorAll(selector).forEach(callback);
}

function setText(selector, text) {
  forEachNode(selector, (element) => {
    element.textContent = text;
  });
}

function setHidden(selector, hidden) {
  forEachNode(selector, (element) => {
    element.hidden = hidden;
  });
}

function setHiddenField(id, value) {
  const element = document.getElementById(id);
  if (element) element.dataset.value = value;
}

function updateDiscountUi(totals) {
  const discountCents = totals.discount || 0;
  const hasDiscount = Number(discountCents) !== 0;
  const percentOff = totals.percentOff;
  const hasPercentOff = percentOff != null && Number(percentOff) !== 0;
  const showPercentOff = hasDiscount && hasPercentOff;

  setText(CHECKOUT_TOTALS.discount, formatMoney(discountCents));

  if (totals.subtotal != null) {
    setText(CHECKOUT_TOTALS.compareAtPrice, formatMoney(totals.subtotal));
    setText(
      CHECKOUT_TOTALS.priceAfterDiscount,
      formatMoney((totals.subtotal || 0) + discountCents)
    );
  }

  setHidden(CHECKOUT_TOTALS.shownWithDiscount, !hasDiscount);
  setHidden(CHECKOUT_TOTALS.shownWithoutDiscount, hasDiscount);
  setHidden(
    `${CHECKOUT_TOTALS.savePercent}, ${CHECKOUT_TOTALS.percentOffBadge}`,
    !showPercentOff
  );

  forEachNode(CHECKOUT_TOTALS.percentOffText, (element) => {
    element.hidden = !showPercentOff;
    if (showPercentOff) {
      const template = element.dataset.checkoutPercent || 'X%';
      element.textContent = template.replaceAll('X', String(percentOff));
    }
  });

  if (showPercentOff) {
    setText(CHECKOUT_TOTALS.savePercent, `Save ${percentOff}%`);
    setText(CHECKOUT_TOTALS.percentOffBadge, `${percentOff}% OFF`);
    setText(
      CHECKOUT_TOTALS.discountLabel,
      `First order discount (${percentOff}%)`
    );
  } else {
    setText(CHECKOUT_TOTALS.discountLabel, 'First order discount');
  }
}

function updateDeliveryFeeUi(totals) {
  const feeCents = Number(totals.deliveryFee);
  const feeLabel =
    typeof totals.deliveryFeeLabel === 'string'
      ? totals.deliveryFeeLabel
      : '';
  const hasDeliveryFee =
    Number.isFinite(feeCents) && feeCents > 0 && feeLabel.trim().length > 0;
  const displayedFeeCents = hasDeliveryFee ? feeCents : 0;
  const displayedFeeLabel = hasDeliveryFee ? feeLabel : '';

  setText(CHECKOUT_TOTALS.deliveryFeeAmount, formatMoney(displayedFeeCents));
  setText(CHECKOUT_TOTALS.deliveryFeeLabel, displayedFeeLabel);
  setHidden(CHECKOUT_TOTALS.deliveryFeeRow, !hasDeliveryFee);
  setHiddenField('hidden-delivery-fee', displayedFeeCents);
  setHiddenField('hidden-delivery-fee-label', displayedFeeLabel);
}

function syncHiddenTotals(totals) {
  setHiddenField('hidden-order-total', totals.orderTotal);
  setHiddenField('hidden-tax', totals.tax);
  setHiddenField('hidden-shipping', totals.shipping);
  setHiddenField('hidden-subtotal', totals.subtotal);
  if (totals.discount != null) {
    setHiddenField('hidden-discount', totals.discount);
  }
}

export function updateCheckoutTotals(totals) {
  if (!totals) return;

  const totalCents =
    totals.amount != null ? totals.amount : totals.orderTotal;

  setText(CHECKOUT_TOTALS.tax, formatMoney(totals.tax || 0));
  setText(CHECKOUT_TOTALS.orderTotal, formatMoney(totalCents));
  setText(CHECKOUT_TOTALS.shipping, formatShipping(totals.shipping));
  updateDiscountUi(totals);
  updateDeliveryFeeUi(totals);
  syncHiddenTotals(totals);

  document.dispatchEvent(
    new CustomEvent('checkout-tax:updated', { detail: totals })
  );
}

export const CHECKOUT_ADDRESS_FIELD_IDS = {
  street_address: 'cart_mailing_address_attributes_street_address',
  city: 'cart_mailing_address_attributes_city',
  state: 'cart_mailing_address_attributes_state',
  zip_code: 'cart_mailing_address_attributes_zip_code',
  suite: 'cart_mailing_address_attributes_suite',
};

export const CHECKOUT_ADDRESS_INPUT_IDS = Object.values(
  CHECKOUT_ADDRESS_FIELD_IDS
);

export function formatAddressFromForm() {
  const address = {};

  Object.entries(CHECKOUT_ADDRESS_FIELD_IDS).forEach(([key, id]) => {
    const value = document.getElementById(id)?.value?.trim();
    if (value) address[key] = value;
  });

  return address;
}

export function isCheckoutAddressComplete(address) {
  return (
    address.street_address &&
    address.city &&
    address.state &&
    address.state.length >= 2 &&
    address.zip_code &&
    address.zip_code.length >= 5
  );
}

export async function fetchCheckoutTax(url, address) {
  const token = document.querySelector('meta[name="csrf-token"]')?.content;
  const body = new FormData();

  Object.entries(address).forEach(([key, value]) => {
    body.append(key, value);
  });

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'X-CSRF-Token': token,
      Accept: 'application/json',
    },
    body,
    credentials: 'same-origin',
  });

  let totals;
  try {
    totals = await response.json();
  } catch {
    return null;
  }

  if (!response.ok) return null;

  return totals;
}
